import { SearchParamsValue } from '@apps/stays/components/SearchParamsObserver/SearchParamsObserver';
import { calculateStayDates } from '@shared/utils/dateHelpers';
import { isBefore, isToday, isFuture, format } from 'date-fns';
import { DEFAULT_OCCUPANCY, QUERY_PARAM_KEYS } from './constants';
import { PICKED_BY_COUPLE_KEY } from '../useListingFilters';
import { createQueryParamsUpdater } from '@apps/accommodations/AccommodationsConsole.utils';
import { History, Location } from 'history';

export type QueryParamsUrlUpdater = (args: {
  pickedByCouple?: boolean | null;
  dates?: { start: string; end: string };
  map?: { destination?: string | null; lat: number; lng: number; radius?: number };
  rooms?: Array<{ adults: number; childrenAges: number[] }>;

  actionType: 'replace' | 'push';
}) => void;

export const createSearchParamsUrlUpdater = (location: Location, history: History): QueryParamsUrlUpdater => args => {
  const params = new URLSearchParams(location.search);
  const updateQueryParams = createQueryParamsUpdater(params);

  if (args.pickedByCouple !== undefined) {
    updateQueryParams(PICKED_BY_COUPLE_KEY, args.pickedByCouple ? 'true' : 'false');
  }

  // Update dates
  if (args.dates) {
    updateQueryParams(QUERY_PARAM_KEYS.startDate, args.dates.start);
    updateQueryParams(QUERY_PARAM_KEYS.endDate, args.dates.end);
  }

  // Update map location
  if (args.map) {
    updateQueryParams(QUERY_PARAM_KEYS.destination, args.map.destination ?? null);
    updateQueryParams(QUERY_PARAM_KEYS.lat, args.map.lat.toString());
    updateQueryParams(QUERY_PARAM_KEYS.lng, args.map.lng.toString());
    typeof args.map.radius === 'number' && updateQueryParams(QUERY_PARAM_KEYS.radius, args.map.radius.toString());
  }

  // Update rooms
  if (args.rooms) {
    updateQueryParams(QUERY_PARAM_KEYS.rooms, args.rooms.map(_roomToQueryString).join(','));
  }

  history[args.actionType]?.({
    pathname: location.pathname,
    search: params.toString()
  });
};

//================================================
// Derive search date query params
//================================================

const toDateObject = (date: { year: number; month: number; day: number }) => {
  return new Date(date.year, date.month - 1, date.day);
};

type DeriveSearchDateQueryParamsArgs = {
  dateSearchParams: Pick<SearchParamsValue, 'startDate' | 'endDate'>;
  eventFinalizedDateString: Maybe<string>;
  useDefaults?: boolean;
};

export const deriveSearchDateQueryParams = ({ dateSearchParams, eventFinalizedDateString, useDefaults }: DeriveSearchDateQueryParamsArgs) => {
  // Date search params are valid (i.e. not null or undefined, and are dates)
  if (!useDefaults && dateSearchParams.endDate.valid && dateSearchParams.startDate.valid) {
    const startDate = toDateObject(dateSearchParams.startDate.value);
    const endDate = toDateObject(dateSearchParams.endDate.value);

    // Confirm that:
    // 1. Start date is either today or in the future
    // 2. End date is after start date
    if ((isToday(startDate) || isFuture(startDate)) && isBefore(startDate, endDate)) {
      return {
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd'),
        isMissingInUrl: false
      };
    }
  }

  // Fallback strategy when date search params are not valid:
  // 1. Use event finalized date
  // 2. If no event date, choose dates 1 month from today
  const { checkIn: eventStartDate, checkOut: eventEndDate } = calculateStayDates(eventFinalizedDateString);

  return {
    startDate: eventStartDate.dateString,
    endDate: eventEndDate.dateString,
    isMissingInUrl: true
  };
};

//================================================
// Derive rooms query params
//================================================
const _roomToQueryString = (room: { adults: number; childrenAges?: number[] }) => {
  return `a${room.adults}${room.childrenAges?.length ? `:c${room.childrenAges.join(':c')}` : ''}`;
};

type UseDeriveRoomsQueryParamsArgs = {
  roomsSearchParams: Pick<SearchParamsValue, 'rooms'>;
  useDefaults: boolean;
};
export const deriveRoomsQueryParams = ({ roomsSearchParams, useDefaults }: UseDeriveRoomsQueryParamsArgs) => {
  if (!useDefaults && roomsSearchParams.rooms.valid) {
    return {
      rooms: roomsSearchParams.rooms.value,
      isMissingInUrl: false
    };
  }

  return {
    rooms: DEFAULT_OCCUPANCY.occupancies,
    isMissingInUrl: true
  };
};
